import { pipe } from 'fp-ts/lib/function';
import { FC, ReactNode, createContext, useEffect, useReducer } from 'react';

import {
  addUserInterest,
  deleteUserInterest,
  fetchUserFavorites,
} from '@/shared/api/user';
import { isLoggedIn } from '@/shared/cookies';
import { FavoriteId } from '@/shared/models/me';
import { nullableData } from '@/shared/utils/http';
import { isInBuilder } from '@/shared/utils/isInBuilder';

export enum LoadingState {
  IDLE = 'idle',
  LOADING = 'loading',
  INIT = 'init',
}

interface LotFavorite {
  lotId: number;
  count: number;
  isCurrentUserFavorite: boolean;
}

export interface InitialLotFavorite {
  lotId: number;
  count: number;
}

const favoriteStatusCache = new Map<number, boolean>();
const pendingFavoriteRequests = new Map<
  number,
  {
    resolve: (favorites: FavoriteId[]) => void;
    lotIds: number[];
  }
>();
let favoriteRequestBatchId = 0;
let favoriteRequestTimer: ReturnType<typeof setTimeout> | undefined;

const resolveCachedFavorites = (lotIds: number[]) =>
  lotIds
    .filter((lotId) => favoriteStatusCache.get(lotId))
    .map((lotId) => ({ lot_id: lotId, id: lotId }));

const setCachedFavoriteStatus = (lotId: number, isFavorite: boolean) => {
  favoriteStatusCache.set(lotId, isFavorite);
};

async function flushPendingFavoriteRequests() {
  favoriteRequestTimer = undefined;
  const requests = Array.from(pendingFavoriteRequests.values());
  pendingFavoriteRequests.clear();

  const lotIds = Array.from(
    new Set(requests.flatMap((request) => request.lotIds))
  );

  const eitherFavorites = await fetchUserFavorites(lotIds);
  const favorites = pipe(eitherFavorites, nullableData);

  if (!favorites) {
    lotIds.forEach((lotId) => {
      favoriteStatusCache.delete(lotId);
    });
    requests.forEach(({ lotIds: requestLotIds, resolve }) => {
      resolve(resolveCachedFavorites(requestLotIds));
    });
    return;
  }

  const favoriteIds = new Set(favorites.map((favorite) => favorite.lot_id));
  lotIds.forEach((lotId) => {
    setCachedFavoriteStatus(lotId, favoriteIds.has(lotId));
  });

  requests.forEach(({ lotIds: requestLotIds, resolve }) => {
    resolve(resolveCachedFavorites(requestLotIds));
  });
}

async function fetchBatchedUserFavorites(
  lotIds: number[]
): Promise<FavoriteId[]> {
  const uncachedLotIds = lotIds.filter(
    (lotId) => !favoriteStatusCache.has(lotId)
  );

  if (uncachedLotIds.length === 0) {
    return resolveCachedFavorites(lotIds);
  }

  return new Promise<FavoriteId[]>((resolve) => {
    favoriteRequestBatchId += 1;
    pendingFavoriteRequests.set(favoriteRequestBatchId, {
      resolve,
      lotIds,
    });

    if (!favoriteRequestTimer) {
      favoriteRequestTimer = setTimeout(() => {
        flushPendingFavoriteRequests().catch(() => undefined);
      }, 0);
    }
  });
}

export function resetUserFavoritesRequestCacheForTests() {
  favoriteStatusCache.clear();
  pendingFavoriteRequests.clear();
  if (favoriteRequestTimer) {
    clearTimeout(favoriteRequestTimer);
    favoriteRequestTimer = undefined;
  }
  favoriteRequestBatchId = 0;
}

const loadFavoriteAction = () => ({
  type: 'load' as const,
});
type LoadFavoriteAction = ReturnType<typeof loadFavoriteAction>;

const initUserFavoriteAction = (favorites: LotFavorite[]) => ({
  type: 'init' as const,
  favorites,
});
type InitUserFavoriteAction = ReturnType<typeof initUserFavoriteAction>;

const addFavoriteAction = (lotId: number) => ({
  type: 'add' as const,
  lotId,
});
type AddFavoriteAction = ReturnType<typeof addFavoriteAction>;

const deleteFavoriteAction = (lotId: number) => ({
  type: 'delete' as const,
  lotId,
});
type DeleteFavoriteAction = ReturnType<typeof deleteFavoriteAction>;

const cancelFavoriteAction = () => ({
  type: 'cancel' as const,
});
type CancelFavoriteAction = ReturnType<typeof cancelFavoriteAction>;

interface UserLotFavoritesState {
  lotFavorites: {
    [key in number]: LotFavorite;
  };
  state: LoadingState;
}

interface UserLotFavoritesContext extends UserLotFavoritesState {
  addFavorite?: (lotId: number) => Promise<void>;
  deleteFavorite?: (lotId: number) => Promise<void>;
}

const initialState: UserLotFavoritesState = {
  lotFavorites: [],
  state: LoadingState.INIT,
};

export const UserLotFavoritesContext =
  createContext<UserLotFavoritesContext>(initialState);

interface Props {
  lotIds: number[];
  initialFavorites: InitialLotFavorite[];
  previewOnly?: boolean;
  children: ReactNode;
}

type Actions =
  | LoadFavoriteAction
  | InitUserFavoriteAction
  | AddFavoriteAction
  | DeleteFavoriteAction
  | CancelFavoriteAction;

export const UserLotFavoritesProvider: FC<Props> = ({
  lotIds,
  initialFavorites,
  previewOnly = false,
  children,
}) => {
  const [favoritesState, dispatch] = useReducer<
    UserLotFavoritesState,
    [Actions]
  >((previousState, action) => {
    switch (action.type) {
      case 'load':
        return { ...previousState, state: LoadingState.LOADING };
      case 'init': {
        const lotFavorites = action.favorites.reduce(
          (result, item) => ({
            ...result,
            [item.lotId]: item,
          }),
          {}
        );

        return {
          ...previousState,
          state: LoadingState.IDLE,
          lotFavorites,
        };
      }
      case 'add': {
        const currentFavorite = previousState.lotFavorites[action.lotId] ?? {
          lotId: action.lotId,
          count: 0,
          isCurrentUserFavorite: false,
        };
        const lotFavorites = {
          ...previousState.lotFavorites,
          [action.lotId]: {
            ...currentFavorite,
            count: currentFavorite.count + 1,
            isCurrentUserFavorite: true,
          },
        };
        return {
          ...previousState,
          state: LoadingState.IDLE,
          lotFavorites,
        };
      }
      case 'delete': {
        const currentFavorite = previousState.lotFavorites[action.lotId];
        if (!currentFavorite) {
          return {
            ...previousState,
            state: LoadingState.IDLE,
          };
        }

        const lotFavorites = {
          ...previousState.lotFavorites,
          [action.lotId]: {
            ...currentFavorite,
            count: Math.max(0, currentFavorite.count - 1),
            isCurrentUserFavorite: false,
          },
        };
        return {
          ...previousState,
          state: LoadingState.IDLE,
          lotFavorites,
        };
      }
      case 'cancel': {
        return {
          ...previousState,
          state: LoadingState.IDLE,
        };
      }
      default:
        return previousState;
    }
  }, initialState);

  const addFavorite = async (lotId: number) => {
    if (previewOnly) {
      if (favoritesState.lotFavorites[lotId]?.isCurrentUserFavorite) return;
      dispatch(addFavoriteAction(lotId));
      return;
    }

    if (!isLoggedIn()) return;
    if (favoritesState.lotFavorites[lotId].isCurrentUserFavorite) return;

    dispatch(loadFavoriteAction());
    try {
      await addUserInterest(lotId);
      setCachedFavoriteStatus(lotId, true);
      dispatch(addFavoriteAction(lotId));
    } catch {
      dispatch(cancelFavoriteAction());
    }
  };

  const deleteFavorite = async (lotId: number) => {
    if (previewOnly) {
      if (!favoritesState.lotFavorites[lotId]?.isCurrentUserFavorite) return;
      dispatch(deleteFavoriteAction(lotId));
      return;
    }

    if (!isLoggedIn()) return;
    if (!favoritesState.lotFavorites[lotId].isCurrentUserFavorite) return;

    dispatch(loadFavoriteAction());
    try {
      await deleteUserInterest(lotId);
      setCachedFavoriteStatus(lotId, false);
      dispatch(deleteFavoriteAction(lotId));
    } catch {
      dispatch(cancelFavoriteAction());
    }
  };

  useEffect(() => {
    (async () => {
      if (previewOnly) {
        const initialFavoritesMap = new Map(
          initialFavorites.map((favorite) => [favorite.lotId, favorite.count])
        );
        dispatch(
          initUserFavoriteAction(
            lotIds.map((lotId) => ({
              lotId,
              count: initialFavoritesMap.get(lotId) ?? 0,
              isCurrentUserFavorite: false,
            }))
          )
        );
        return;
      }

      // Disable all calls inside builder.io
      if (isInBuilder()) {
        dispatch(
          initUserFavoriteAction(
            lotIds.map((lotId) => ({
              lotId,
              count: 10,
              isCurrentUserFavorite: false,
            }))
          )
        );
        return;
      }

      const initialFavoritesMap = new Map(
        initialFavorites.map((favorite) => [favorite.lotId, favorite.count])
      );
      const initFavorites = lotIds.map((lotId) => ({
        lotId,
        count: initialFavoritesMap.get(lotId) ?? 0,
        isCurrentUserFavorite: false,
      }));

      if (!isLoggedIn()) {
        dispatch(initUserFavoriteAction(initFavorites));
        return;
      }

      const favorites = await fetchBatchedUserFavorites(lotIds);
      dispatch(
        initUserFavoriteAction(
          initFavorites.map((favorite) => ({
            lotId: favorite.lotId,
            count: favorite.count,
            isCurrentUserFavorite: Boolean(
              favorites.find((item) => item.lot_id === favorite.lotId)
            ),
          }))
        )
      );
    })();
  }, [initialFavorites, lotIds, previewOnly]);

  return (
    <UserLotFavoritesContext.Provider
      value={{ ...favoritesState, addFavorite, deleteFavorite }}
    >
      {children}
    </UserLotFavoritesContext.Provider>
  );
};
